import { useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import {
    motion,
    useScroll,
    useTransform,
    useMotionValueEvent,
} from "framer-motion";
import * as THREE from "three";
import { sceneProgress } from "@/lib/scrollState";

const Core = () => {
    const outer = useRef(null);
    const inner = useRef(null);
    const smooth = useRef(0);

    useFrame((state, delta) => {
        smooth.current = THREE.MathUtils.damp(smooth.current, sceneProgress.value, 4, delta);
        const p = smooth.current;
        if (outer.current) {
            outer.current.rotation.y = p * Math.PI * 2.5;
            outer.current.rotation.x = p * Math.PI * 1.2;
            outer.current.scale.setScalar(1 + p * 1.5);
        }
        if (inner.current) {
            inner.current.rotation.y = -p * Math.PI * 4;
            inner.current.rotation.z = p * Math.PI;
            inner.current.scale.setScalar(
                0.55 + Math.sin(state.clock.elapsedTime * 1.4) * 0.04,
            );
        }
    });

    return (
        <group>
            <mesh ref={outer}>
                <icosahedronGeometry args={[1.7, 1]} />
                <meshBasicMaterial color="#52525b" wireframe transparent opacity={0.5} />
            </mesh>
            <mesh ref={inner}>
                <octahedronGeometry args={[0.95, 0]} />
                <meshStandardMaterial color="#fafafa" metalness={0.9} roughness={0.18} flatShading />
            </mesh>
            <pointLight position={[4, 4, 4]} intensity={60} color="#ffffff" />
            <pointLight position={[-4, -2, 3]} intensity={25} color="#c7d2fe" />
            <ambientLight intensity={0.2} />
        </group>
    );
};

const PHRASES = [
    { word: "STRATEGY.", caption: "We map the money before we touch a pixel." },
    { word: "SYSTEMS.", caption: "Websites, funnels and automations built as one machine." },
    { word: "SCALE.", caption: "Campaigns live, data watched, growth compounded." },
];

export const ScrollScene = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end end"],
    });
    useMotionValueEvent(scrollYProgress, "change", (v) => {
        sceneProgress.value = v;
    });

    const o1 = useTransform(scrollYProgress, [0.02, 0.14, 0.3], [0, 1, 0]);
    const y1 = useTransform(scrollYProgress, [0.02, 0.14, 0.3], [40, 0, -40]);
    const o2 = useTransform(scrollYProgress, [0.34, 0.46, 0.62], [0, 1, 0]);
    const y2 = useTransform(scrollYProgress, [0.34, 0.46, 0.62], [40, 0, -40]);
    const o3 = useTransform(scrollYProgress, [0.66, 0.78, 0.96], [0, 1, 1]);
    const y3 = useTransform(scrollYProgress, [0.66, 0.78], [40, 0]);
    const barScale = useTransform(scrollYProgress, [0, 1], [0, 1]);

    const opacities = [o1, o2, o3];
    const offsets = [y1, y2, y3];

    return (
        <section ref={ref} data-testid="scroll-scene" className="relative h-[320vh]">
            <div className="sticky top-0 h-screen overflow-hidden">
                <div className="pointer-events-none absolute inset-0">
                    <Canvas
                        dpr={[1, 2]}
                        camera={{ position: [0, 0, 5.6], fov: 45 }}
                        gl={{ antialias: true, alpha: true }}
                    >
                        <Core />
                    </Canvas>
                </div>
                {PHRASES.map((phrase, i) => (
                    <motion.div
                        key={phrase.word}
                        style={{ opacity: opacities[i], y: offsets[i] }}
                        className="absolute inset-0 flex flex-col justify-center px-6 md:px-12"
                    >
                        <h2
                            className={`font-display text-[16vw] font-black uppercase leading-none tracking-tighter md:text-[11vw] ${
                                i % 2 === 1 ? "text-outline" : "text-white"
                            }`}
                        >
                            {phrase.word}
                        </h2>
                        <p className="font-mono-x mt-6 max-w-md text-xs uppercase tracking-[0.25em] text-zinc-400 md:text-sm">
                            {phrase.caption}
                        </p>
                    </motion.div>
                ))}
                <div className="absolute right-6 top-1/2 h-32 w-px -translate-y-1/2 bg-white/10 md:right-12">
                    <motion.div
                        style={{ scaleY: barScale }}
                        className="h-full w-full origin-top bg-white"
                    />
                </div>
            </div>
        </section>
    );
};
